import {
  Ban,
  CheckCircle2,
  CircleDashed,
  Clock,
  Hand,
  Loader2,
  MinusCircle,
  ShieldAlert,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { SurfaceTone } from '@/components/surface/tones';
import type { PlaybookSummary, Run, RunStatus, TargetStatus } from '@/types/api';

/**
 * How runs and servers read at a glance. Every status carries an icon and a word as well as a colour:
 * the failure is the thing somebody most needs to see, and colour alone does not reach everyone.
 */

export const runTone: Record<RunStatus, SurfaceTone> = {
  queued: 'muted',
  checking: 'primary',
  awaiting_confirmation: 'warning',
  running: 'primary',
  succeeded: 'success',
  partially_failed: 'warning',
  failed: 'danger',
  cancelled: 'muted',
};

export const runIcon: Record<RunStatus, LucideIcon> = {
  queued: Clock,
  checking: Loader2,
  awaiting_confirmation: Hand,
  running: Loader2,
  succeeded: CheckCircle2,
  partially_failed: TriangleAlert,
  failed: XCircle,
  cancelled: Ban,
};

export const targetTone: Record<TargetStatus, SurfaceTone> = {
  pending: 'muted',
  checking: 'primary',
  ready: 'primary',
  blocked: 'danger',
  queued: 'muted',
  running: 'primary',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'muted',
  skipped: 'muted',
};

export const targetIcon: Record<TargetStatus, LucideIcon> = {
  pending: CircleDashed,
  checking: Loader2,
  ready: CheckCircle2,
  blocked: ShieldAlert,
  queued: Clock,
  running: Loader2,
  succeeded: CheckCircle2,
  failed: XCircle,
  cancelled: Ban,
  skipped: MinusCircle,
};

/** Statuses whose icon spins, because something is happening right now. */
export function isWorking(status: RunStatus | TargetStatus): boolean {
  return status === 'checking' || status === 'running';
}

/** A run the page should keep polling. */
export function isLive(run: Pick<Run, 'is_finished'>): boolean {
  return !run.is_finished;
}

export const categoryLabel: Record<PlaybookSummary['category'], string> = {
  services: 'Services',
  packages: 'Packages',
  maintenance: 'Maintenance',
  runtimes: 'Runtimes',
  web: 'Web servers',
  databases: 'Databases',
  monitoring: 'Monitoring',
  security: 'Security',
};

/** The order categories appear in: what most people reach for first. */
export const categoryOrder: PlaybookSummary['category'][] = [
  'services',
  'packages',
  'maintenance',
  'runtimes',
  'web',
  'databases',
  'monitoring',
  'security',
];

/** "8s", "4m 12s", "1h 03m". Null while a run has not started. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return 'Not started';
  }

  if (seconds < 60) {
    return `${seconds}s`;
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;

    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/** "3 of 5 servers succeeded, 1 failed", from a run's per-status counts. */
export function countsSentence(run: Run): string {
  const counts = run.target_counts ?? {};
  const parts: string[] = [];
  const add = (count: number | undefined, label: string) => {
    if (count !== undefined && count > 0) {
      parts.push(`${count} ${label}`);
    }
  };

  add(counts.succeeded, 'succeeded');
  add(counts.failed, 'failed');
  add(counts.blocked, 'blocked');
  add(counts.cancelled, 'cancelled');
  add(counts.skipped, 'skipped');

  const working = (counts.checking ?? 0) + (counts.running ?? 0);
  add(working, 'in progress');

  const waiting = (counts.pending ?? 0) + (counts.ready ?? 0) + (counts.queued ?? 0);
  add(waiting, 'waiting');

  return parts.length === 0 ? `${run.total_targets} server${run.total_targets === 1 ? '' : 's'}` : parts.join(', ');
}
